import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Copy, KeyRound, Plus, Undo2, UserPlus } from 'lucide-react';
import { adminAPI } from '../api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from './ui/alert-dialog';
import { PENDING, PERIOD_WORDING, isReceived, longDate, money, stateLabel, todayIso } from '../lib/subscription';
import { copyText } from '../lib/clipboard';

const detailOf = (error) => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Erreur';
};

// The temporary password is in the answer once; the page shows it once and keeps it nowhere.
const TemporaryPassword = ({ shown, onClose }) => (
  <Dialog open={Boolean(shown)} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Mot de passe temporaire</DialogTitle>
        <DialogDescription>
          Pour {shown?.who}. Il ne sera plus affiché : transmettez-le à la personne. Elle choisira le sien à sa première connexion.
        </DialogDescription>
      </DialogHeader>
      <div className="flex items-center gap-2">
        <Input readOnly value={shown?.password ?? ''} className="font-mono" data-testid="temporary-password" />
        <Button
          variant="outline"
          onClick={async () => {
            try {
              await copyText(shown.password);
              toast.success('Copié');
            } catch {
              toast.error('Copie refusée par le navigateur : sélectionnez le mot de passe et copiez-le');
            }
          }}
          data-testid="copy-temporary-password"
        >
          <Copy className="w-4 h-4 mr-2" />
          Copier
        </Button>
      </div>
      <DialogFooter>
        <Button onClick={onClose} data-testid="close-temporary-password">Fermer</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// The subscription of one organisation: its state, its reference and total to match a transfer against, and the
// admin's record of the payment, the proof for the incubator (docs/specs/subscription-page, 2a).
const Payment = ({ organisation, onChanged }) => {
  const [receivedOn, setReceivedOn] = useState(todayIso());
  const { subscription, payment } = organisation;
  const received = isReceived(payment);
  const inputId = `payment-date-${organisation.id}`;

  const record = async () => {
    try {
      await adminAPI.recordPayment(organisation.id, receivedOn);
      toast.success('Paiement reçu inscrit');
      onChanged();
    } catch (error) {
      toast.error(detailOf(error));
    }
  };

  const undo = async () => {
    try {
      await adminAPI.undoPayment(organisation.id);
      toast.success('Paiement annulé');
      onChanged();
    } catch (error) {
      toast.error(detailOf(error));
    }
  };

  return (
    <div className="flex flex-wrap items-end justify-between gap-3 pl-4" data-testid="organisation-payment">
      <div className="space-y-1">
        <Badge variant={received ? 'default' : 'secondary'} data-testid="payment-state">
          {received && <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />}
          {stateLabel(payment)}
        </Badge>
        <p className="text-xs text-muted-foreground">
          Référence <span className="font-mono" data-testid="payment-reference">{subscription.reference}</span>
          {' · '}{money(subscription.monthly_total_cents, subscription.terms.currency)} {PERIOD_WORDING[subscription.terms.period]}
          {received && <> · inscrit par {payment.recorded_by_email}</>}
        </p>
      </div>
      {received ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" data-testid="undo-payment">
              <Undo2 className="w-4 h-4 mr-1" aria-hidden="true" />
              Annuler le paiement
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Annuler le paiement de {organisation.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                Le paiement reçu le {longDate(payment.received_on)} sera retiré et l'organisation repassera « En attente du paiement ».
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Garder le paiement</AlertDialogCancel>
              <AlertDialogAction onClick={undo} data-testid="confirm-undo-payment">Annuler le paiement</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : (
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor={inputId}>Reçu le</Label>
            <Input id={inputId} type="date" max={todayIso()} className="w-40" value={receivedOn}
              onChange={(e) => setReceivedOn(e.target.value)} data-testid="payment-date" />
          </div>
          <Button onClick={record} disabled={!receivedOn} data-testid="record-payment">Paiement reçu</Button>
        </div>
      )}
    </div>
  );
};

// The trial's end date, which an admin may change (docs/specs/rc7, decision c): what the member reads, and who
// changed it and when, which only the admin reads.
const TrialEnd = ({ organisation, onChanged }) => {
  const { subscription, trial_end: change } = organisation;
  const [endsOn, setEndsOn] = useState(subscription.trial_ends_on || '');
  const inputId = `trial-end-date-${organisation.id}`;

  const save = async () => {
    try {
      await adminAPI.changeTrialEnd(organisation.id, endsOn);
      toast.success('Fin de l\'essai changée');
      onChanged();
    } catch (error) {
      toast.error(detailOf(error));
    }
  };

  return (
    <div className="flex flex-wrap items-end justify-between gap-3 pl-4" data-testid="organisation-trial-end">
      <div className="space-y-1">
        <p className="text-sm">
          Fin de l'essai :{' '}
          <span data-testid="trial-end-shown">
            {subscription.trial_ends_on ? longDate(subscription.trial_ends_on) : PENDING}
          </span>
        </p>
        {change && (
          <p className="text-xs text-muted-foreground" data-testid="trial-end-changed-by">
            changée par {change.changed_by_email} le {longDate(change.changed_at.slice(0, 10))}
          </p>
        )}
      </div>
      <div className="flex items-end gap-2">
        <div className="space-y-1">
          <Label htmlFor={inputId}>Nouvelle fin</Label>
          <Input id={inputId} type="date" min={subscription.trial_starts_on} className="w-40" value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)} data-testid="trial-end-date" />
        </div>
        <Button variant="outline" onClick={save} disabled={!endsOn} data-testid="change-trial-end">Changer la fin de l'essai</Button>
      </div>
    </div>
  );
};

const Organisation = ({ organisation, onChanged, onTemporary }) => {
  const [seats, setSeats] = useState(String(organisation.seats));
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const full = organisation.seats_used >= organisation.seats;

  useEffect(() => setSeats(String(organisation.seats)), [organisation.seats]);

  const saveSeats = async () => {
    try {
      await adminAPI.updateOrganisationSeats(organisation.id, Number(seats));
      toast.success('Sièges mis à jour');
      onChanged();
    } catch (error) {
      toast.error(detailOf(error));
      setSeats(String(organisation.seats));
    }
  };

  const addMember = async () => {
    try {
      const { data } = await adminAPI.addMember(organisation.id, { full_name: fullName, email });
      onTemporary({ who: data.member.full_name, password: data.temporary_password });
      setFullName('');
      setEmail('');
      onChanged();
    } catch (error) {
      toast.error(detailOf(error));
    }
  };

  const reset = async (member) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${member.full_name}?`)) return;
    try {
      const { data } = await adminAPI.resetPassword(member.id);
      onTemporary({ who: member.full_name, password: data.temporary_password });
      onChanged();
    } catch (error) {
      toast.error(detailOf(error));
    }
  };

  return (
    <div className="py-4 space-y-3" data-testid={`organisation-${organisation.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-medium" data-testid="organisation-name">{organisation.name}</span>
          <Badge variant={full ? 'secondary' : 'outline'} data-testid="organisation-seats">
            {organisation.seats_used} de {organisation.seats} sièges
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Input type="number" min="1" className="w-20" value={seats}
            onChange={(e) => setSeats(e.target.value)} data-testid="organisation-seats-input" />
          <Button variant="outline" onClick={saveSeats} data-testid="organisation-seats-save">Changer les sièges</Button>
        </div>
      </div>
      <Payment organisation={organisation} onChanged={onChanged} />
      <TrialEnd organisation={organisation} onChanged={onChanged} />
      <div className="divide-y pl-4">
        {organisation.members.map((member) => (
          <div key={member.id} className="flex items-center justify-between py-2" data-testid={`member-${member.id}`}>
            <div>
              <p className="text-sm font-medium">{member.full_name}</p>
              <p className="text-xs text-muted-foreground">{member.email}</p>
            </div>
            <div className="flex items-center gap-2">
              {member.must_change_password && <Badge variant="secondary">Mot de passe temporaire</Badge>}
              <Button variant="ghost" size="sm" onClick={() => reset(member)} data-testid={`reset-password-${member.id}`}>
                <KeyRound className="w-4 h-4 mr-1" />
                Réinitialiser le mot de passe
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-end gap-2 pl-4">
        <div className="space-y-1">
          <Label>Nom complet</Label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} data-testid="member-name" />
        </div>
        <div className="space-y-1">
          <Label>Courriel</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="member-email" />
        </div>
        <Button onClick={addMember} disabled={!fullName || !email} data-testid="add-member-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Ajouter un membre
        </Button>
      </div>
    </div>
  );
};

const OrganisationsPanel = () => {
  const [organisations, setOrganisations] = useState([]);
  const [name, setName] = useState('');
  const [seats, setSeats] = useState('1');
  const [temporary, setTemporary] = useState(null);

  const load = useCallback(async () => {
    try {
      setOrganisations((await adminAPI.listOrganisations()).data);
    } catch (error) {
      toast.error(detailOf(error));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await adminAPI.createOrganisation({ name, seats: Number(seats) });
      toast.success('Organisation créée');
      setName('');
      setSeats('1');
      load();
    } catch (error) {
      toast.error(detailOf(error));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading uppercase">Organisations</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-2 mb-4">
          <div className="space-y-1">
            <Label>Nom</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="organisation-new-name" />
          </div>
          <div className="space-y-1">
            <Label>Sièges</Label>
            <Input type="number" min="1" className="w-20" value={seats}
              onChange={(e) => setSeats(e.target.value)} data-testid="organisation-new-seats" />
          </div>
          <Button onClick={create} disabled={!name} className="btn-primary" data-testid="add-organisation-btn">
            <Plus className="w-4 h-4 mr-2" />
            Créer l'organisation
          </Button>
        </div>
        <div className="divide-y">
          {organisations.map((organisation) => (
            <Organisation key={organisation.id} organisation={organisation} onChanged={load} onTemporary={setTemporary} />
          ))}
          {organisations.length === 0 && (
            <p className="text-center py-8 text-muted-foreground">Aucune organisation</p>
          )}
        </div>
      </CardContent>
      <TemporaryPassword shown={temporary} onClose={() => setTemporary(null)} />
    </Card>
  );
};

export default OrganisationsPanel;
